/**
 * AdminLockedFieldset / AdminLockException — Locked mode for Admin forms
 * (plan 2026-10-10-001, KTD5 and KTD6). Both are FieldLockScopes
 * (components/ui/FieldLock.tsx): the shared inputs inside read the scope,
 * show their values and refuse changes, and are never greyed out or labelled
 * as locked (R18).
 *
 *   AdminLockedFieldset  wraps a settings-style page's form. While the page is
 *                        Locked its fields are read-only; while Editable it
 *                        adds nothing (no scope, so it changes no behaviour).
 *                        It renders no element of its own, so it never
 *                        changes the page's layout, and it only covers what
 *                        it wraps — keep the page's search and filters
 *                        outside it. The page still drops its own Save,
 *                        Connect and Upload buttons at Locked.
 *
 *   AdminLockException   marks the fields a Locked page may still change (a
 *                        named Locked exception, e.g. a product's facilities
 *                        and store switch). Inside a locked AdminFormModal or
 *                        AdminLockedFieldset its fields are editable again; at
 *                        Editable it changes nothing. Pair it with
 *                        AdminFormModal's `lockedSave` to keep Save.
 */
import type { ReactNode } from 'react';
import { FieldLockScope } from '@/components/ui/FieldLock';
import { useAdminPageLevel } from './AdminPageLevelContext';

export function AdminLockedFieldset({ children }: { children: ReactNode }) {
  const locked = useAdminPageLevel() === 'locked';
  // Always the same Provider, so a level change on an open page does not remount
  // the form (locked={false} equals the default outside any scope).
  return <FieldLockScope locked={locked}>{children}</FieldLockScope>;
}

export function AdminLockException({ children }: { children: ReactNode }) {
  return <FieldLockScope locked={false}>{children}</FieldLockScope>;
}
